'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { TAnalyticArgs } from '@/containers/AnalyticsContainer';

type RouteInfo = {
  labels: string[];
  analyticsEvent?: TAnalyticArgs[0];
};

type UseGetRouteAnalyticsReturn = {
  labels: string[];
  analyticsEvent?: TAnalyticArgs[0];
};

const routeStoryDataMap: Record<string, RouteInfo> = {
  '/': { labels: ['All', 'Home', 'خانه'] },
  '/auth/complete-register': {
    labels: ['All', 'Reg_incomplete', 'تکمیل ثبت نام '],
  },
  '/auto-invest': {
    labels: ['All', 'Auto_Invest', 'auto-invest', 'سرمایه گذاری مستمر'],
  },
  '/card/deposit': {
    labels: ['All', 'Withdraw_card', 'برداشت مفید کارت'],
  },
  '/card/settings': {
    labels: ['All', 'Card_settings', 'تنظیمات مفید کارت'],
  },
  '/card/transactions': {
    labels: ['All', 'Card_trx', 'تراکنش های مفید کارت'],
  },
  '/card/transfer': {
    labels: ['All', 'C2C', 'کارت به کارت'],
  },
  '/card/withdraw': {
    labels: ['All', 'Deposite_card', 'واریز از کارت'],
  },
  '/deposit': {
    labels: ['All', 'Deposite_online', 'واریز آنلاین (ثبت فیش/درگاه)'],
  },
  '/invest/fund': {
    labels: ['All', 'Fund', 'صندوق‌ها'],
  },
  '/invest/fund/10600': {
    labels: ['All', 'Pishtaz', 'پیشتاز'],
  },
  '/invest/fund/10762': {
    labels: ['All', 'Momtaz', 'ممتاز'],
  },
  '/invest/fund/10851': {
    labels: ['All', 'Pishro', 'پیشرو'],
  },
  '/invest/fund/11132': {
    labels: ['All', 'Omid', 'امید'],
  },
  '/invest/fund/11562': {
    labels: ['All', 'Nami', 'نامی'],
  },
  '/invest/fund/12021': {
    labels: ['All', 'Tazmin', 'تضمین'],
  },
  '/invest/fund/12033': {
    labels: ['All', 'Tavan_normal', 'توان عادی'],
  },
  '/invest/fund/IRT1ARAM0001': {
    labels: ['All', 'Aram', 'آرام'],
  },
  '/invest/fund/IRT1STIL0001': {
    labels: ['All', 'Steel', 'استیل'],
  },
  '/invest/fund/IRT3AVNF0001': {
    labels: ['All', 'Avand', 'آوند'],
  },
  '/invest/fund/IRT3SATF0001': {
    labels: ['All', 'Atlas', 'اطلس'],
  },
  '/invest/fund/IRT3TVAF0001': {
    labels: ['All', 'Tavan_leverage', 'توان اهرمی'],
  },
  '/invest/fund/IRTKMOFD0001': {
    labels: ['All', 'Ayar', 'عیار'],
  },
  '/invest/fund/issuance': {
    labels: ['All', 'Fund_issuance', 'خرید صندوق'],
  },
  '/invest/fund/redemption': {
    labels: ['All', 'Fund_redemption', 'فروش صندوق'],
  },
  '/invest/fund/transaction': {
    labels: ['All', 'Fund_trx', 'تراکنش صندوق'],
  },
  '/invest/investment-advisory': {
    labels: ['All', 'investment_advisory', 'سبد منتخب سرمایه‌گذاری'],
    analyticsEvent: 'ia_click_hint',
  },
  '/invest/investment-advisory/issuance': {
    labels: ['All', 'investment_advisory', 'سبد منتخب سرمایه‌گذاری'],
    analyticsEvent: 'ia_click_hint',
  },
  '/invest/robo-advisor': {
    labels: ['All', 'Robo', 'روبو'],
  },
  '/invest/child': {
    labels: ['All', 'child_investment', 'سرمایه‌گذاری برای فرزند'],
  },
  '/invest/child/add': {
    labels: ['All', 'add_child_investment', 'اضافه کردن فرزند'],
  },
  '/pfm': {
    labels: ['All', 'PFM', 'مدیریت مالی'],
  },
  '/profile': {
    labels: ['All', 'Profile', 'پروفایل'],
  },
  '/profile/contracts': {
    labels: ['All', 'Contracts', 'قراردادها'],
  },
  '/profile/inbox': {
    labels: ['All', 'Messages', 'پیام‌ها'],
  },
  '/profile/security-settings': {
    labels: ['All', 'Security-settings', 'تنظیمات امنیتی'],
  },
  '/profile/user-information': {
    labels: ['All', 'User_information', 'اطلاعات کاربر'],
  },
  '/stock': {
    labels: ['All', 'Stock', 'سبد سهام'],
  },
  '/stock/[stockCode]': {
    labels: ['All', 'Stock_details', 'جزییات سهام'],
  },
  '/transactions': {
    labels: ['All', 'Hami_trx', 'تراکنش‌های حامی'],
  },
  '/withdraw': {
    labels: ['All', 'Withdraw_bank', 'برداشت بانک'],
  },
};

export const useGetRouteStoryData = (
  isChangelog: boolean = false
): UseGetRouteAnalyticsReturn => {
  const pathname = usePathname(); // Equivalent to `route`
  const searchParams = useSearchParams(); // Helps to construct the `asPath`

  const stringedSearchParams = searchParams?.toString()
    ? `?${searchParams.toString()}`
    : '';

  const asPath = pathname + stringedSearchParams;

  if (isChangelog) {
    return {
      labels: ['All', 'Change_log', 'تغییرات'],
      analyticsEvent: undefined,
    };
  }
  if (asPath === '/?index=1') {
    return {
      labels: ['All', 'Mofid_card', 'مفید کارت'],
      analyticsEvent: undefined,
    };
  }
  const routeInfo = pathname ? routeStoryDataMap[pathname] : undefined;

  return {
    labels: routeInfo?.labels || ['All'],
    analyticsEvent: routeInfo?.analyticsEvent,
  };
};
